/**
 * Viewport click handler for edit mode — the main dispatch between
 * Alt-click (loop), Ctrl-click (shortest path), Shift-click (additive),
 * and plain click (replace) selection, plus a few intercepts (extrude
 * pending, curve shortcut, edge-loop-insert pick mode).
 *
 * Split into its own module because `handleEditClick` is by far the
 * largest single method on the edit slice — keeping it standalone
 * makes the selection module readable.
 */

import {
  captureEditSelectionForRecall,
  clearEditSelection,
  deriveVerticesFromFaces,
  hasAnySelection,
  sanitizeLastActiveMarkers,
  startSelectionSet,
  toggleSelection,
  toggleSelectionBatch,
} from "../../modeling/editSelection.ts";
import { insertEdgeLoop as meshInsertEdgeLoop } from "../../modeling/meshOps.ts";
import {
  edgeNeighbors,
  edgeVertices,
  faceNeighbors,
  findEdgeIndex,
  findShortestEdgePath,
  findShortestFacePath,
  findShortestPath,
  getAdjacencyCache,
  topologyFaceVerts,
  vertexNeighbors,
} from "../../modeling/topology.ts";
import { walkLoop, walkRing } from "../../modeling/walkers.ts";
import {
  captureSelectionDeltaState,
  pushSelectionUndo,
  undoManager,
} from "../storeUndo.ts";
import { notifyTopologyOpFailure } from "../topologyOpNotifications.ts";
import {
  applyResult,
  type EditClickHit,
  type EditClickModifiers,
  type Get_,
  replaceSceneObjectAfterEdit,
  type Set_,
} from "./core.ts";

export function createClickOps(set: Set_, get: Get_) {
  return {
    handleEditClick: (hit: EditClickHit, modifiers: EditClickModifiers) => {
      const liveEm = get().editMode;
      if (!liveEm) return;
      const obj = get().sceneObjects.get(liveEm.objectId);
      if (!obj) return;

      // Drop any `lastActive*` markers pointing past the end of the current
      // topology before any shortest-path/loop walk reads them. Adjacency
      // lookups tolerate stale indices, but a null marker makes the fallback
      // to plain selection explicit instead of relying on empty-neighbor luck.
      const em = sanitizeLastActiveMarkers(
        liveEm,
        obj.topology,
        obj.vertices.length / 3,
      );

      // --- Intercept: extrude/extend pending (tapped on existing geometry) ---
      // Execute the extrude and consume the click — don't fall through to selection.
      if (em.extrudePending) {
        set({
          editMode: { ...em, extrudePending: false },
          activeOperator: null,
        });
        if (em.isCurve) {
          get().extendCurve();
        } else {
          get().extrudeSelection();
        }
        return;
      }

      // --- Curve shortcut: simple vertex selection only ---
      if (em.isCurve) {
        get().pushUndo();
        get().selectVertices(hit.vertices, modifiers.shift);
        return;
      }

      // --- Intercept: edge loop insert "pick edge" mode ---
      const cb = get().commandBuffer;
      if (cb?.type === "edgeLoopInsert" && cb.edgeLoopInsertEdge == null) {
        // The clicked edge IS the ring direction. The ring walks along it,
        // and the cut goes perpendicular (splitting the cross edges).
        if (!hit.edge) return;
        const [v0, v1] = hit.edge;

        const cuts = (cb.params?.cuts as number) ?? 1;
        const testResult = meshInsertEdgeLoop(
          obj.vertices,
          obj.indices,
          obj.topology,
          v0,
          v1,
          0.5,
          cuts,
        );
        if (!testResult) {
          notifyTopologyOpFailure(
            "edge_loop_insert",
            "Edge Loop Insert requires an edge on a cuttable ring.",
          );
          return;
        }
        const loopNewVerts = testResult.newSelection;
        set({
          ...replaceSceneObjectAfterEdit(
            get(),
            em.objectId,
            applyResult(obj, testResult, "insertEdgeLoop"),
          ),
          editMode: {
            ...clearEditSelection(em),
            selectedVertices: loopNewVerts,
          },
          commandBuffer: {
            ...cb,
            edgeLoopInsertEdge: [v0, v1],
            edgeLoopHoverEdge: null,
            edgeLoopPreview: null,
            boneCommandData: null,
            proportionalWeights: null,
            mouseOrigin: null,
          },
        });
        return;
      }

      // All remaining paths are selection-only. Capture before/after via
      // a lightweight SelectionDelta so we don't deep-clone the whole
      // scene (and the active edit mesh) for a click. Try/finally
      // brackets every early-return branch below so the delta is
      // always pushed regardless of which selection path runs.
      // Save the current selection for recall whenever a non-additive (non-shift)
      // click is about to replace it. Shift always extends; any other modifier
      // combination can replace the entire selection.
      if (!modifiers.shift && hasAnySelection(em)) {
        set({ lastEditModeSelection: captureEditSelectionForRecall(em) });
      }
      const selectionDeltaBefore = captureSelectionDeltaState(get());
      try {
        const granularity = em.granularity;

        // --- Alt+Click: loop selection (respects granularity) ---
        // Matches Blender: Alt+Click replaces, Shift+Alt+Click is additive.
        if (modifiers.alt) {
          let edgeA: number, edgeB: number;
          if (hit.edge) {
            [edgeA, edgeB] = hit.edge;
          } else if (em.lastActiveVertex != null) {
            // Vertex mode: find edge between active vertex and clicked vertex
            const altCache = getAdjacencyCache(
              obj.topology,
              obj.vertices.length / 3,
            );
            const activeGroup = [em.lastActiveVertex];
            const clickedSet = new Set(hit.vertices);
            let found = false;
            edgeA = -1;
            edgeB = -1;
            for (const av of activeGroup) {
              const neighbors = vertexNeighbors(altCache, obj.topology, av);
              for (const n of neighbors) {
                if (clickedSet.has(n)) {
                  edgeA = av;
                  edgeB = n;
                  found = true;
                  break;
                }
              }
              if (found) break;
            }
            if (!found) {
              return get().selectVertices(hit.vertices, modifiers.shift);
            }
          } else {
            return get().selectVertices(hit.vertices, modifiers.shift);
          }

          // Face loop = edge ring walk (opposite edges across quads → face strip).
          // Edge loop = through-vertex walk (shares no face → edge path).
          // Blender uses ring traversal for face loops and loop traversal for edge loops.
          const walkCache = getAdjacencyCache(
            obj.topology,
            obj.vertices.length / 3,
          );
          const edgeIdx = findEdgeIndex(obj.topology, edgeA!, edgeB!);
          const loop =
            granularity === "face"
              ? walkRing(obj.topology, walkCache, edgeIdx, 0, obj.vertices)
              : walkLoop(obj.topology, walkCache, edgeIdx, 0, obj.vertices);

          // Select based on granularity
          if (granularity === "face" && loop.faces.length > 0) {
            // Face loop: derive vertices strictly from the face set
            const loopFaces = startSelectionSet(
              em.selectedFaces,
              modifiers.shift,
            );
            for (const f of loop.faces) loopFaces.add(f);
            const sel = deriveVerticesFromFaces(obj.topology, loopFaces);
            set({
              editMode: {
                ...em,
                selectedVertices: sel,
                selectedEdges: new Set(),
                selectedFaces: loopFaces,
                lastActiveVertex: hit.vertices[0],
                lastActiveEdge: hit.edge
                  ? findEdgeIndex(obj.topology, hit.edge[0], hit.edge[1])
                  : em.lastActiveEdge,
                lastActiveFace: hit.faceIndex ?? em.lastActiveFace,
              },
            });
          } else {
            // Vertex and edge mode: select the edge loop vertices
            const expanded = loop.vertices;
            const sel = startSelectionSet(em.selectedVertices, modifiers.shift);
            for (const v of expanded) sel.add(v);
            // In edge mode, track explicit edges so perpendicular shared-vertex
            // edges aren't inferred as selected from vertex membership alone.
            const newEdges =
              granularity === "edge"
                ? (() => {
                    const edges = startSelectionSet(
                      em.selectedEdges,
                      modifiers.shift,
                    );
                    for (const ei of loop.edges) {
                      if (ei >= 0) edges.add(ei);
                    }
                    return edges;
                  })()
                : new Set<number>();
            set({
              editMode: {
                ...em,
                selectedVertices: sel,
                selectedEdges: newEdges,
                lastActiveVertex: hit.vertices[0],
                lastActiveEdge: hit.edge
                  ? findEdgeIndex(obj.topology, hit.edge[0], hit.edge[1])
                  : em.lastActiveEdge,
                lastActiveFace: hit.faceIndex ?? em.lastActiveFace,
              },
            });
          }
          return;
        }

        // --- Ctrl+Click: shortest path (granularity-aware) ---
        // Matches Blender: Ctrl+Click replaces, Ctrl+Shift+Click is additive.
        // When no lastActive exists, falls through to normal selection so the
        // click still registers (establishes the active element for next path).
        if (modifiers.ctrl) {
          let pathHandled = false;
          if (granularity === "edge" && em.lastActiveEdge != null && hit.edge) {
            // Edge-mode: shortest edge path
            const edgeCache = getAdjacencyCache(
              obj.topology,
              obj.vertices.length / 3,
            );
            const targetEdgeIdx = findEdgeIndex(
              obj.topology,
              hit.edge[0],
              hit.edge[1],
            );
            const edgePath = findShortestEdgePath(
              (e: number) => edgeNeighbors(edgeCache, obj.topology, e),
              em.lastActiveEdge,
              targetEdgeIdx,
            );
            if (edgePath.length > 0) {
              const sel = startSelectionSet(
                em.selectedVertices,
                modifiers.shift,
              );
              const pathEdges = startSelectionSet(
                em.selectedEdges,
                modifiers.shift,
              );
              for (const ei of edgePath) {
                const [a, b] = edgeVertices(obj.topology.edges, ei);
                sel.add(a);
                sel.add(b);
                pathEdges.add(ei);
              }
              set({
                editMode: {
                  ...em,
                  selectedVertices: sel,
                  selectedEdges: pathEdges,
                  lastActiveVertex: hit.vertices[0],
                  lastActiveEdge: targetEdgeIdx,
                },
              });
              pathHandled = true;
            }
          } else if (
            granularity === "face" &&
            em.lastActiveFace != null &&
            hit.faceIndex != null
          ) {
            // Face-mode: shortest face path
            const faceCache = getAdjacencyCache(
              obj.topology,
              obj.vertices.length / 3,
            );
            const facePath = findShortestFacePath(
              (f: number) => faceNeighbors(faceCache, obj.topology, f),
              em.lastActiveFace,
              hit.faceIndex,
            );
            if (facePath.length > 0) {
              const faceSet = startSelectionSet(
                em.selectedFaces,
                modifiers.shift,
              );
              for (const fi of facePath) faceSet.add(fi);
              // Derive vertices strictly from the face set
              const sel = deriveVerticesFromFaces(obj.topology, faceSet);
              set({
                editMode: {
                  ...em,
                  selectedVertices: sel,
                  selectedFaces: faceSet,
                  lastActiveVertex: hit.vertices[0],
                  lastActiveFace: hit.faceIndex,
                },
              });
              pathHandled = true;
            }
          } else if (em.lastActiveVertex != null) {
            // Vertex-mode: shortest vertex path
            const vertCache = getAdjacencyCache(
              obj.topology,
              obj.vertices.length / 3,
            );
            const sourceGroup = [em.lastActiveVertex];
            const targetGroup = hit.vertices;
            const path = findShortestPath(
              (v: number) => vertexNeighbors(vertCache, obj.topology, v),
              sourceGroup,
              targetGroup,
            );
            if (path.length > 0) {
              const sel = startSelectionSet(
                em.selectedVertices,
                modifiers.shift,
              );
              for (const v of path) sel.add(v);
              set({
                editMode: {
                  ...em,
                  selectedVertices: sel,
                  lastActiveVertex: hit.vertices[0],
                },
              });
              pathHandled = true;
            }
          }
          if (pathHandled) return;
          // No lastActive or path not found — fall through to normal selection
          // so the click still establishes an active element for the next path.
        }

        // --- Normal click (with optional Shift for additive) ---
        const additive = modifiers.shift || modifiers.ctrl;
        let targetVerts: number[];
        let newActiveEdge: number | null = additive ? em.lastActiveEdge : null;
        let newActiveFace: number | null = additive ? em.lastActiveFace : null;
        let newSelectedFaces: Set<number> | undefined;
        let newSelectedEdges: Set<number> | undefined;
        if (granularity === "face" && hit.faceIndex != null) {
          const faceVerts = topologyFaceVerts(obj.topology, hit.faceIndex);
          targetVerts = faceVerts;
          newActiveFace = hit.faceIndex;
          // Track explicit face selection so shared-vertex faces don't
          // appear highlighted when only their neighbors are selected.
          newSelectedFaces = toggleSelection(
            em.selectedFaces,
            hit.faceIndex,
            additive,
          );
        } else if (granularity === "edge" && hit.edge) {
          targetVerts = [hit.edge[0], hit.edge[1]];
          const hitEdgeIdx = findEdgeIndex(
            obj.topology,
            hit.edge[0],
            hit.edge[1],
          );
          newActiveEdge = hitEdgeIdx >= 0 ? hitEdgeIdx : null;
          // Track explicit edge selection so shared-vertex perpendicular edges
          // aren't inferred as selected from vertex membership alone.
          if (hitEdgeIdx >= 0) {
            if (additive) {
              newSelectedEdges = new Set(em.selectedEdges);
              if (
                newSelectedEdges.has(hitEdgeIdx) &&
                targetVerts.every((v) => em.selectedVertices.has(v))
              ) {
                newSelectedEdges.delete(hitEdgeIdx);
              } else {
                newSelectedEdges.add(hitEdgeIdx);
              }
            } else {
              newSelectedEdges = new Set([hitEdgeIdx]);
            }
          }
        } else {
          targetVerts = hit.vertices;
        }

        // In face mode, selectedVertices is always derived from selectedFaces.
        // This prevents shared-vertex ambiguity: the vertex set is the exact
        // union of selected faces' vertices, nothing more.
        // In vertex/edge mode, selectedVertices is accumulated directly.
        let newSel: Set<number>;
        if (granularity === "face" && newSelectedFaces) {
          newSel = deriveVerticesFromFaces(obj.topology, newSelectedFaces);
        } else if (granularity === "edge" && newSelectedEdges) {
          // In edge mode, derive vertex set from explicit edges to avoid
          // accumulating stale vertex membership from prior non-edge selections.
          newSel = new Set<number>();
          for (const ei of newSelectedEdges) {
            const [a, b] = edgeVertices(obj.topology.edges, ei);
            newSel.add(a);
            newSel.add(b);
          }
        } else {
          newSel = toggleSelectionBatch(
            em.selectedVertices,
            targetVerts,
            additive,
          );
        }
        set({
          editMode: {
            ...em,
            selectedVertices: newSel,
            selectedEdges:
              newSelectedEdges ??
              (granularity !== "edge" ? new Set() : em.selectedEdges),
            selectedFaces: newSelectedFaces ?? em.selectedFaces,
            lastActiveVertex: targetVerts[0] ?? em.lastActiveVertex,
            lastActiveEdge: newActiveEdge,
            lastActiveFace: newActiveFace,
          },
        });
      } finally {
        const selectionDeltaAfter = captureSelectionDeltaState(get());
        pushSelectionUndo(selectionDeltaBefore, selectionDeltaAfter);
        set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
      }
    },
  };
}
